import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import SearchIcon from "@mui/icons-material/Search";
import { nullCheck } from "../../../utils/dataHelpers";
import { Typography } from "@mui/material";
import isEmailValidator from "validator/lib/isEmail";
import NumberFormat from "react-number-format";
import StyledTextField from "../StyledTextField/StyledTextField";
import StyledFormHelperText from "../StyledFormHelperText/StyledFormHelperText";

const NumberInputCustom = React.forwardRef(function NumberInputCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={values => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
      thousandSeparator
    />
  );
});

NumberInputCustom.propTypes = {
  onChange: PropTypes.func,
};

export default function PrimaryTextField({
  id,
  label,
  onChange,
  onBlur,
  width,
  fullWidth,
  value,
  placeholder,
  onEnter,
  disabled,
  error,
  setError,
  isNumber,
  isInteger,
  maxLength,
  isPhone,
  isCurrency,
  isEmail,
  isSearch,
  customPadding,
  multiline,
  rows,
  enableIncrement,
  endAdormentButton,
  helperText,
  maxValue,
  tooltip,
  readOnly
}) {
  function phoneFormat(input) {
    input = input.replace(/\D/g, "");
    let size = input.length;
    if (size > 3) { input = input.slice(0, 3) + "-" + input.slice(3, 10); }
    if (size > 6) { input = input.slice(0, 7) + "-" + input.slice(7); }
    return input;
  }

  function validateEmail(input) {
    setError(isEmailValidator(input));
    return input;
  }

  function validateCurrency(input) {
    return input.indexOf(".") >= 0 ? (input.substr(0, input.indexOf(".")) + input.substr(input.indexOf("."), 3)) : input;
  }

  const handleChange = (event) => {
    const value = event.target.value;

    if (isPhone) {
      onChange(phoneFormat(value));
    } else if (isEmail) {
      onChange(validateEmail(value));
    } else if (isCurrency) {
      onChange(validateCurrency(value));
    } else if (isInteger) {
      if (parseInt(value, 10) > parseInt(maxValue, 10)) {
        onChange(maxValue);
      } else {
        onChange(value);
      }
    }
    else {
      onChange(value);
    }
  };

  const handleIncrement = () => {
    if (!value) {
      value = 0;
    }
    onChange(parseInt(value) + 1);
  };

  const handleDecrement = () => {
    if (!value) {
      value = 0;
    }
    onChange(parseInt(value) - 1);
  };

  const handleKeyDown = (event) => {
    if ((isNumber &&
      (/\D/gm.test(event.key) &&
        event.key !== "Backspace" &&
        event.key !== "-" &&
        event.key !== "Tab" &&
        event.key !== "Shift" &&
        event.key !== "ArrowLeft" &&
        event.key !== "ArrowRight" &&
        event.key !== ".")
    ) || (isInteger &&
      (/\D/gm.test(event.key) &&
        event.key !== "Backspace" &&
        event.key !== "Tab" &&
        event.key !== "ArrowLeft" &&
        event.key !== "ArrowRight")
    )) {
      event.preventDefault();
    }

    if (event.key === "Enter" && onEnter) {
      onEnter();
    }

    if (isInteger && enableIncrement && event.key === "ArrowUp") {
      handleIncrement();
    } else if (isInteger && enableIncrement && event.key === "ArrowDown") {
      handleDecrement();
    }
  };

  const handleMouseHover = (event) => {
    if (tooltip) {
      event.target.title = tooltip;
    }
  };

  return (
    <Box>
      <StyledTextField
        id={id}
        variant="outlined"
        label={label}
        onChange={handleChange}
        onBlur={onBlur}
        value={nullCheck(value, "")}
        placeholder={placeholder}
        size="small"
        type="text"
        multiline={multiline}
        rows={rows}
        fullWidth={fullWidth}
        sx={{ width: width }}
        inputProps={{
          sx: {
            padding: "4px 8px",
            ...customPadding && { padding: `${customPadding.vertical} ${customPadding.horizontal}` }
          },
          readOnly: readOnly,
          inputMode: "numeric",
          pattern: "[0-9]*",
          ...maxLength && { maxLength: maxLength }
        }}
        InputProps={{
          ...isSearch && {
            startAdornment:
              <InputAdornment
                position="start">
                <SearchIcon sx={{ color: "black_solid.60" }} />
              </InputAdornment>
          },
          ...isCurrency && {
            inputComponent: NumberInputCustom,
            startAdornment:
              <InputAdornment
                position="start">
                <Typography color="black_solid.50">
                  $
                </Typography>
              </InputAdornment>
          },
          ...isInteger && enableIncrement && {
            sx: { paddingRight: "3px" },
            inputComponent: NumberInputCustom,
            endAdornment:
              <InputAdornment
                position="end">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <ArrowDropUpIcon
                    onClick={handleIncrement}
                    sx={{ color: "black_solid.60", "&:hover": { cursor: "pointer" }, marginBottom: "-10px" }}
                  />
                  <ArrowDropDownIcon
                    onClick={handleDecrement}
                    sx={{ color: "black_solid.60", "&:hover": { cursor: "pointer" } }} />
                </Box>
              </InputAdornment>
          },
          ...endAdormentButton && {
            endAdornment:
              <InputAdornment
                position="end">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <ArrowCircleRightIcon
                    onClick={onEnter}
                    sx={{
                      color: disabled ? "black_solid.30" : "black_solid.60",
                      "&:hover": {
                        ...(!disabled && { cursor: "pointer" })
                      }
                    }}
                  />
                </Box>
              </InputAdornment>
          }
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        error={error}
        onMouseEnter={handleMouseHover}
      >
      </StyledTextField>
      <StyledFormHelperText error={error} disabled={disabled}>{helperText}</StyledFormHelperText>
    </Box>
  );
}

PrimaryTextField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  width: PropTypes.string,
  fullWidth: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  placeholder: PropTypes.string,
  onEnter: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  setError: PropTypes.func,
  isNumber: PropTypes.bool,
  isInteger: PropTypes.bool,
  maxLength: PropTypes.string,
  isPhone: PropTypes.bool,
  isCurrency: PropTypes.bool,
  isEmail: PropTypes.bool,
  isSearch: PropTypes.bool,
  enableIncrement: PropTypes.bool,
  customPadding: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  endAdormentButton: PropTypes.bool,
  helperText: PropTypes.string,
  maxValue: PropTypes.string,
  tooltip: PropTypes.string,
  readOnly: PropTypes.bool
};